import * as React from 'react';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageSection, Tooltip } from '@patternfly/react-core';
import {
    PencilAltIcon,
    CalendarAltIcon,
    BuildingIcon,
    ArrowLeftIcon,
    TrashIcon,
    CubesIcon,
    MapMarkerIcon,
    RouteIcon,
    CrosshairsIcon,
    PlayIcon,
    BanIcon,
    ClockIcon,
    CheckCircleIcon,
    ExpandArrowsAltIcon,
    CompressArrowsAltIcon,
    CarIcon,
    UsersIcon,
    PauseIcon,
    TimesIcon
} from '@patternfly/react-icons';
import { TrailReplay, TrailReplayHandle } from './TrailReplay';
import {
    GoogleMap,
    LoadScript,
    Marker,
    Circle,
    Polyline
} from '@react-google-maps/api';
import {
    getCampaignById,
    deleteCampaign,
    stopCampaign,
    updateCampaign,
    getCampaignRuns,
    cancelRun,
    CampaignRun
} from '@app/utils/utils';
import { getCampaignTypeConfig } from './campaignTypeUtils';
import { decodePolyline } from './polylineUtils';
import { splitTrailAtGaps } from './trailGaps';
import { TargetReachProgress } from './TargetReachProgress';
import { EditCampaign } from './EditCampaign';
import { ScheduleRunsStep } from './ScheduleRunsStep';
import { AppModal } from '@app/components/AppModal';
import toast from 'react-hot-toast';

const libraries: ("places")[] = ["places"];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Feature flag (build-time, injected via webpack DefinePlugin from env/*.js):
// gates the per-campaign Measured Reach section. Off in production until launch.
const MEASURED_REACH_ENABLED = process.env.FEATURE_MEASURED_REACH === 'true';

// Distinct colors so overlapping GPS trails from different runs stay readable
const RUN_TRAIL_COLORS = ['#f97316', '#ef4444', '#06b6d4', '#84cc16', '#ec4899', '#eab308', '#14b8a6', '#a855f7'];

const METERS_PER_MILE = 1609.34;

// Lenient JSON parse for backend "*Json"/measuredReach string columns: never throws.
const safeParseJson = (raw: any): any => {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
};

// "high_income" -> "High income"
const formatCohortKey = (key: string): string =>
    key.split('_').join(' ').replace(/^\w/, (c) => c.toUpperCase());

// Shared confidence color ramp (also used by the TargetReach route-plan stats)
const confidenceColor = (score: number): string =>
    score >= 0.7 ? '#059669' : score >= 0.4 ? '#d97706' : '#dc2626';

// One label/value line inside a Measured Reach metric card (label left, value right).
const metricRow = (label: string, value: React.ReactNode, headline = false): React.ReactElement => (
    <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '12px',
        padding: headline ? '0 0 10px 0' : '8px 0 0 0',
        borderTop: headline ? 'none' : '1px solid #f1f5f9',
    }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <span style={{ fontSize: headline ? '20px' : '14px', fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
);

// Column layout shared by the per-run breakdown header + rows.
const perRunGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(130px, 1.4fr) 1fr 1fr 0.8fr 1fr',
    gap: '12px',
    alignItems: 'baseline',
};

const CampaignDetailPage: React.FunctionComponent = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Stop state
    const [showStopConfirm, setShowStopConfirm] = useState(false);
    const [isStopping, setIsStopping] = useState(false);

    // Activation flow state
    const [showActivateFlow, setShowActivateFlow] = useState(false);

    // Runs state
    const [runs, setRuns] = useState<CampaignRun[]>([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [cancellingRunId, setCancellingRunId] = useState<number | null>(null);
    const [runToCancel, setRunToCancel] = useState<CampaignRun | null>(null);

    // Add more runs modal
    const [showScheduleRunsModal, setShowScheduleRunsModal] = useState(false);

    // Route selection state
    const [selectedRouteUuid, setSelectedRouteUuid] = useState<string | null>(null);
    const [isSavingRoute, setIsSavingRoute] = useState(false);
    const [routeSaved, setRouteSaved] = useState(false);

    // Map state
    const [mapLoaded, setMapLoaded] = useState(false);
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    // GPS trail visibility: runIds the user has toggled off in the legend
    const [hiddenRunIds, setHiddenRunIds] = useState<Set<number>>(new Set());

    // Trail replay state: one run at a time is "focused" and animated — the
    // trail draws itself while the ad-wrapped car sprite drives along it.
    const replayRef = useRef<TrailReplayHandle | null>(null);
    const [activeReplayRunId, setActiveReplayRunId] = useState<number | null>(null);
    const [replayPlaying, setReplayPlaying] = useState(false);
    const [replaySpeed, setReplaySpeed] = useState(1);
    const [replayProgress, setReplayProgress] = useState(0);
    const [replayTotalMs, setReplayTotalMs] = useState(0);
    const [replayWallMs, setReplayWallMs] = useState<number | null>(null);
    // Remaining runIds when replaying the whole campaign chronologically
    const replayQueueRef = useRef<number[]>([]);

    const fetchCampaign = () => {
        if (!campaignId) return;
        setIsLoading(true);
        getCampaignById(campaignId).then((result) => {
            if (result) {
                setCampaign(result);
                setError(false);
            } else {
                setError(true);
            }
            setIsLoading(false);
        });
    };

    const fetchRuns = () => {
        if (!campaignId) return;
        setRunsLoading(true);
        getCampaignRuns(campaignId)
            .then((rs) => setRuns(rs || []))
            .catch(() => { /* keep stale */ })
            .finally(() => setRunsLoading(false));
    };

    useEffect(() => {
        fetchCampaign();
        fetchRuns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    // Poll runs while any are in a non-terminal approval/run state.
    const RUN_NON_TERMINAL_APPROVAL = new Set(['PENDING_APPROVAL', 'APPROVED']);
    const RUN_NON_TERMINAL_STATE = new Set(['PENDING', 'IN_PROGRESS', 'PAUSED']);
    useEffect(() => {
        if (!campaignId) return;
        const hasNonTerminal = runs.some(
            (r) =>
                RUN_NON_TERMINAL_APPROVAL.has(r.approvalState) ||
                (r.state && RUN_NON_TERMINAL_STATE.has(r.state))
        );
        if (!hasNonTerminal) return;
        const interval = setInterval(() => {
            getCampaignRuns(campaignId)
                .then((rs) => setRuns(rs || []))
                .catch(() => { /* keep stale */ });
        }, 15000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runs, campaignId]);

    // Strip query string to get the base URL (path without token)
    const getBaseUrl = (url: string) => url.split('?')[0];

    // Compare campaigns ignoring ad transient fields (presignedUrl, thumbnailUrl)
    const campaignChanged = (prev: any, next: any): boolean => {
        const stripAds = (c: any) => {
            const { ads, ...rest } = c;
            return rest;
        };
        return JSON.stringify(stripAds(prev)) !== JSON.stringify(stripAds(next));
    };

    // Poll for non-terminal campaigns to detect state changes
    const isGenerating = campaign?.generationStatus === 'IN_PROGRESS';
    useEffect(() => {
        if (!campaign) return;
        const isTerminalState = ['STOPPED_BY_OWNER'].includes(campaign.currentState);
        if (isTerminalState && !isGenerating) return;
        const pollInterval = isGenerating ? 5000 : 10000;
        const interval = setInterval(() => {
            if (!campaignId) return;
            getCampaignById(campaignId).then((result) => {
                if (result) {
                    // Preserve existing ad presignedUrls if only the token changed
                    if (result.ads && campaign.ads) {
                        const oldUrlsByAdId: Record<string, string> = {};
                        for (const ad of campaign.ads) {
                            if (ad.adId && ad.presignedUrl) {
                                oldUrlsByAdId[ad.adId] = ad.presignedUrl;
                            }
                        }
                        for (const ad of result.ads) {
                            const oldUrl = oldUrlsByAdId[ad.adId];
                            if (oldUrl && ad.presignedUrl && getBaseUrl(oldUrl) === getBaseUrl(ad.presignedUrl)) {
                                ad.presignedUrl = oldUrl;
                            }
                        }
                    }
                    // Only update state if non-ad data actually changed
                    if (campaignChanged(campaign, result)) {
                        setCampaign(result);
                    }
                }
            });
        }, pollInterval);
        return () => clearInterval(interval);
    }, [campaign?.currentState, campaign?.generationStatus, campaignId]);

    // Measured-reach analysis lifecycle across the campaign's runs. IN_PROGRESS
    // drives the "Calculating…" spinner (data isn't there yet); FAILED lets the
    // panel explain why totals are missing instead of spinning forever.
    const measuredReachInProgress = useMemo(
        () => (campaign?.driver?.runs || []).some((r: any) => r.measuredReachStatus === 'IN_PROGRESS'),
        [campaign?.driver?.runs]
    );
    const measuredReachFailed = useMemo(
        () => (campaign?.driver?.runs || []).some((r: any) => r.measuredReachStatus === 'FAILED'),
        [campaign?.driver?.runs]
    );

    // Keep refreshing while a measured-reach analysis is running, so the
    // "Calculating…" spinner resolves to data (or a failure) without a manual
    // reload. The main poll above stops once the campaign reaches a terminal
    // state, but measured reach is computed *after* runs finish — so this needs
    // its own trigger. Mirrors the org-research IN_PROGRESS poll.
    useEffect(() => {
        if (!campaignId || !measuredReachInProgress) return;
        const interval = setInterval(() => {
            getCampaignById(campaignId)
                .then((result) => {
                    if (!result) return;
                    setCampaign((prev: any) => {
                        if (!prev) return result;
                        // Preserve existing ad presignedUrls if only the token changed (avoid image flicker).
                        if (result.ads && prev.ads) {
                            const oldUrlsByAdId: Record<string, string> = {};
                            for (const ad of prev.ads) {
                                if (ad.adId && ad.presignedUrl) oldUrlsByAdId[ad.adId] = ad.presignedUrl;
                            }
                            for (const ad of result.ads) {
                                const oldUrl = oldUrlsByAdId[ad.adId];
                                if (oldUrl && ad.presignedUrl && getBaseUrl(oldUrl) === getBaseUrl(ad.presignedUrl)) {
                                    ad.presignedUrl = oldUrl;
                                }
                            }
                        }
                        return campaignChanged(prev, result) ? result : prev;
                    });
                })
                .catch(() => { /* keep stale */ });
        }, 7000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measuredReachInProgress, campaignId]);

    // Campaign type info
    const typeConfig = campaign ? getCampaignTypeConfig(campaign.campaignType || campaign.type) : null;
    const isGeoFence = campaign && (campaign.campaignType || campaign.type) === 'GeoFenceCampaign';
    const isDrawRoutes = campaign && (campaign.campaignType || campaign.type) === 'DrawRoutesCampaign';
    const isTargetReach = campaign && (campaign.campaignType || campaign.type) === 'TargetReachCampaign';
    const hasTargetReachMap = isTargetReach && campaign.generationStatus === 'COMPLETED' && campaign.routePlans?.length > 0;
    // GPS trails from completed runs are available for every campaign type
    const hasTrail = !!campaign?.driver?.runs?.some((run: any) => run.trackPoints && run.trackPoints.length > 1);
    // A map should render when a campaign has base geometry OR a recorded trail to show
    const hasBaseMap = isGeoFence || isDrawRoutes || hasTargetReachMap;
    const hasMap = hasBaseMap || hasTrail;
    const hasApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;

    // State helpers
    const currentState = campaign?.currentState || 'DRAFT';
    const isDraft = currentState === 'DRAFT';
    // STOPPED_BY_OWNER is the only hard terminal state; everything else
    // (incl. COMPLETED / NEEDS_RUNS) is re-bookable.
    const isTerminal = ['STOPPED_BY_OWNER'].includes(currentState);
    const canSelectRoute = isDraft;

    const getStateColor = (state: string | undefined) => {
        const stateLower = state ? state.toLowerCase() : '';
        switch (stateLower) {
            case 'active': return { bg: '#dcfce7', text: '#166534' };
            case 'draft': return { bg: '#f3f4f6', text: '#374151' };
            case 'completed': return { bg: '#ede9fe', text: '#5b21b6' };
            case 'needs_runs': return { bg: '#fef3c7', text: '#92400e' };
            case 'stopped_by_owner': return { bg: '#fff7ed', text: '#9a3412' };
            case 'error': return { bg: '#fef2f2', text: '#991b1b' };
            default: return { bg: '#eff6ff', text: '#1e40af' };
        }
    };
    const stateColors = campaign ? getStateColor(currentState) : { bg: '#f3f4f6', text: '#374151' };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Map helpers
    // Memoized so the GoogleMap `center` prop keeps a stable reference across
    // re-renders (e.g. toggling trail visibility) and doesn't snap the map back.
    const mapCenter = useMemo(() => {
        if (isGeoFence && campaign?.config?.geoFence) {
            return { lat: campaign.config.geoFence.latitude, lng: campaign.config.geoFence.longitude };
        }
        if (isDrawRoutes && campaign?.config?.drawRoutes?.startingPoint) {
            return { lat: campaign.config.drawRoutes.startingPoint.latitude, lng: campaign.config.drawRoutes.startingPoint.longitude };
        }
        if (isTargetReach && campaign?.config?.targetReach) {
            return { lat: campaign.config.targetReach.startLat, lng: campaign.config.targetReach.startLng };
        }
        return { lat: 37.7749, lng: -122.4194 };
    }, [isGeoFence, isDrawRoutes, isTargetReach, campaign?.config]);

    const getMapZoom = () => {
        if (isGeoFence && campaign.config?.geoFence) {
            const radius = campaign.config.geoFence.radius;
            if (radius > 3000) return 12;
            if (radius > 1500) return 13;
            if (radius > 500) return 14;
            return 15;
        }
        if (isDrawRoutes) return 14;
        if (isTargetReach) return 12;
        return 14;
    };

    const getZoomForDistance = (distanceMeters?: number) => {
        if (!distanceMeters) return 15;
        if (distanceMeters > 10000) return 12;
        if (distanceMeters > 5000) return 13;
        if (distanceMeters > 2000) return 14;
        if (distanceMeters > 1000) return 15;
        if (distanceMeters > 500) return 16;
        return 17;
    };

    const handleCenterMap = () => {
        if (!mapRef.current) return;
        if (isGeoFence && campaign.config?.geoFence) {
            mapRef.current.panTo({ lat: campaign.config.geoFence.latitude, lng: campaign.config.geoFence.longitude });
            const radius = campaign.config.geoFence.radius;
            let zoom = 15;
            if (radius > 3000) zoom = 12;
            else if (radius > 1500) zoom = 13;
            else if (radius > 500) zoom = 14;
            mapRef.current.setZoom(zoom);
        } else if (isDrawRoutes && campaign.config?.drawRoutes) {
            mapRef.current.panTo({ lat: campaign.config.drawRoutes.startingPoint.latitude, lng: campaign.config.drawRoutes.startingPoint.longitude });
            mapRef.current.setZoom(getZoomForDistance(campaign.config.drawRoutes.totalDistanceMeters));
        } else if (isTargetReach && campaign.config?.targetReach && decodedRoutes.length > 0) {
            fitTargetReachBounds();
        } else if (hasTrail) {
            fitTrailBounds();
        }
    };

    // Full-window map: exit on Escape, lock background scroll, and nudge Google Maps
    // to reflow + re-center after the container resizes (it doesn't do so on its own).
    useEffect(() => {
        const timer = setTimeout(() => {
            if (mapRef.current) {
                google.maps.event.trigger(mapRef.current, 'resize');
                handleCenterMap();
            }
        }, 0);

        if (!isMapExpanded) return () => clearTimeout(timer);

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsMapExpanded(false);
        };
        window.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            clearTimeout(timer);
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapExpanded]);

    // Parse resultJson for TargetReach campaigns
    const parsedResult = useMemo(() => {
        if (!isTargetReach || !campaign?.resultJson) return null;
        try {
            return JSON.parse(campaign.resultJson);
        } catch {
            return null;
        }
    }, [isTargetReach, campaign?.resultJson]);

    // Decode route polylines for TargetReach
    const decodedRoutes = useMemo(() => {
        if (!hasTargetReachMap || !campaign.routePlans) return [];
        return campaign.routePlans.map((plan: any) => ({
            ...plan,
            path: plan.polyline ? decodePolyline(plan.polyline, 6) : []
        }));
    }, [hasTargetReachMap, campaign?.routePlans]);

    // GPS trails to draw: assign a stable color by original run order, keep only
    // runs with a real trail, then sort chronologically by their scheduled slot.
    const trailRuns = useMemo(() => {
        const allRuns: any[] = campaign?.driver?.runs || [];
        return allRuns
            .map((run, idx) => {
                const matched = runs.find((r) => r.id === run.runId || r.runId === run.runId);
                // Stable segment/gap references so replay-tick re-renders don't
                // force every <Polyline> to rebuild its point array. Solid
                // segments are recorded driving; gaps are recording holes
                // (GPS/app dropout) drawn dotted instead of as a fake straight drive.
                const { segments, gaps } = splitTrailAtGaps(run.trackPoints || []);
                return {
                    run,
                    color: RUN_TRAIL_COLORS[idx % RUN_TRAIL_COLORS.length],
                    slotStart: matched?.slotStart || null,
                    segments,
                    gaps
                };
            })
            .filter((t) => t.run.trackPoints && t.run.trackPoints.length > 1)
            .sort((a, b) => {
                // Runs without a known slot sort to the end
                const ta = a.slotStart ? new Date(a.slotStart).getTime() : Infinity;
                const tb = b.slotStart ? new Date(b.slotStart).getTime() : Infinity;
                return ta - tb;
            });
    }, [campaign?.driver?.runs, runs]);

    // Campaign-level measured (actual, post-campaign) reach aggregated across runs.
    const measuredReach = useMemo(
        () => safeParseJson(campaign?.measuredReach),
        [campaign?.measuredReach]
    );

    // Per-run measured reach, keeping only runs the worker has already analyzed.
    const perRunMeasuredReach = useMemo(() => {
        const allRuns: any[] = campaign?.driver?.runs || [];
        return allRuns
            .map((run) => ({ run, mr: safeParseJson(run.measuredReach) }))
            .filter((x) => x.mr)
            .sort((a, b) => {
                const ta = a.run.slotStart ? new Date(a.run.slotStart).getTime() : Infinity;
                const tb = b.run.slotStart ? new Date(b.run.slotStart).getTime() : Infinity;
                return ta - tb;
            });
    }, [campaign?.driver?.runs]);

    const toggleRunVisibility = (runId: number) => {
        setHiddenRunIds((prev) => {
            const next = new Set(prev);
            if (next.has(runId)) {
                next.delete(runId);
            } else {
                next.add(runId);
            }
            return next;
        });
    };

    // ---- Trail replay ----

    const activeTrailRun = useMemo(
        () => trailRuns.find((t) => t.run.runId === activeReplayRunId) || null,
        [trailRuns, activeReplayRunId]
    );

    // First ad's creative goes on the car's rooftop LED screen during replay
    const primaryAd = campaign?.ads?.[0];
    const adMediaUrl = primaryAd?.presignedUrl || primaryAd?.thumbnailUrl || null;
    const adIsVideo = !!adMediaUrl && /\.(mp4|webm|mov)(\?|$)/i.test(adMediaUrl);

    // Replay opens at street level on the run's start; from there the
    // chase-cam inside TrailReplay keeps the car in view.
    const REPLAY_ZOOM = 18;
    const centerRunStart = (runId: number) => {
        if (!mapRef.current) return;
        const run = (campaign?.driver?.runs || []).find((r: any) => r.runId === runId);
        const tp = run?.trackPoints?.[0];
        if (!tp) return;
        mapRef.current.setCenter({ lat: tp.latitude, lng: tp.longitude });
        mapRef.current.setZoom(REPLAY_ZOOM);
    };

    const startReplay = (runId: number) => {
        setHiddenRunIds((prev) => {
            if (!prev.has(runId)) return prev;
            const next = new Set(prev);
            next.delete(runId);
            return next;
        });
        setActiveReplayRunId(runId);
        setReplayPlaying(true);
        setReplayProgress(0);
        setReplayWallMs(null);
        centerRunStart(runId);
    };

    const exitReplay = () => {
        replayQueueRef.current = [];
        setActiveReplayRunId(null);
        setReplayPlaying(false);
        setReplayProgress(0);
        handleCenterMap();
    };

    const handleChipClick = (runId: number) => {
        if (runId === activeReplayRunId) {
            exitReplay();
        } else {
            replayQueueRef.current = [];
            startReplay(runId);
        }
    };

    // Replay every visible run back-to-back, oldest first
    const handleReplayAll = () => {
        const ids = trailRuns
            .filter((t) => !hiddenRunIds.has(t.run.runId))
            .map((t) => t.run.runId);
        if (!ids.length) return;
        replayQueueRef.current = ids.slice(1);
        startReplay(ids[0]);
    };

    const handleReplayPlayPause = () => {
        if (!replayPlaying && replayProgress >= 0.999) {
            replayRef.current?.seek(0);
            setReplayProgress(0);
        }
        setReplayPlaying((p) => !p);
    };

    const handleReplaySeek = (p: number) => {
        replayRef.current?.seek(p);
        setReplayProgress(p);
    };

    const handleReplayTick = (p: number, _elapsedMs: number, totalMs: number, wallMs: number | null) => {
        setReplayProgress(p);
        setReplayTotalMs(totalMs);
        setReplayWallMs(wallMs);
    };

    const handleReplayEnded = () => {
        const next = replayQueueRef.current.shift();
        if (next != null) {
            // Brief beat between runs so the hand-off reads as intentional
            setTimeout(() => startReplay(next), 700);
        } else {
            setReplayPlaying(false);
        }
    };

    const cycleReplaySpeed = () => {
        setReplaySpeed((s) => (s === 1 ? 2 : s === 2 ? 4 : 1));
    };

    const formatWallClock = (ms: number) =>
        new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const formatRunDuration = (ms: number) => {
        const mins = Math.round(ms / 60000);
        if (mins < 1) return '<1 min';
        if (mins < 60) return `${mins} min`;
        return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    };

    // Initialize route selection from backend or default to primary
    useEffect(() => {
        if (!decodedRoutes.length) return;
        if (campaign?.selectedRouteUuid) {
            setSelectedRouteUuid(campaign.selectedRouteUuid);
            setRouteSaved(true);
        } else {
            const primary = decodedRoutes.find((r: any) => r.rankOrder === 1);
            setSelectedRouteUuid(primary?.routeUuid || decodedRoutes[0]?.routeUuid || null);
            setRouteSaved(false);
        }
    }, [decodedRoutes, campaign?.selectedRouteUuid]);

    // Auto-fit bounds for TargetReach routes
    const fitTargetReachBounds = () => {
        if (!mapRef.current || !decodedRoutes.length) return;
        const bounds = new google.maps.LatLngBounds();
        if (campaign.config?.targetReach) {
            bounds.extend({ lat: campaign.config.targetReach.startLat, lng: campaign.config.targetReach.startLng });
        }
        decodedRoutes.forEach((route: any) => {
            route.path.forEach((pt: { lat: number; lng: number }) => bounds.extend(pt));
        });
        mapRef.current.fitBounds(bounds, 40);
    };

    useEffect(() => {
        if (mapLoaded && hasTargetReachMap && decodedRoutes.length > 0) {
            fitTargetReachBounds();
        }
    }, [mapLoaded, hasTargetReachMap, decodedRoutes]);

    // Auto-fit bounds to the GPS trail for campaigns without base geometry to anchor on
    const fitTrailBounds = () => {
        if (!mapRef.current || !campaign?.driver?.runs) return;
        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;
        campaign.driver.runs.forEach((run: any) => {
            run.trackPoints?.forEach((tp: any) => {
                bounds.extend({ lat: tp.latitude, lng: tp.longitude });
                hasPoints = true;
            });
        });
        if (hasPoints) {
            mapRef.current.fitBounds(bounds, 40);
        }
    };

    useEffect(() => {
        if (mapLoaded && !hasBaseMap && hasTrail) {
            fitTrailBounds();
        }
    }, [mapLoaded, hasBaseMap, hasTrail]);

    // Action handlers
    const handleDelete = async () => {
        if (!campaign) return;
        setIsDeleting(true);
        try {
            await deleteCampaign(campaign.campaignId);
            toast.success(`"${campaign.name}" deleted`);
            navigate('/campaigns');
        } catch (error: any) {
            console.error('Failed to delete campaign:', error);
            toast.error(error.message || 'Failed to delete campaign');
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleStop = async () => {
        if (!campaign) return;
        setIsStopping(true);
        try {
            await stopCampaign(campaign.campaignId);
            toast.success(`"${campaign.name}" stopped`);
            setCampaign({ ...campaign, currentState: 'STOPPED_BY_OWNER' });
            setShowStopConfirm(false);
        } catch (error: any) {
            console.error('Failed to stop campaign:', error);
            toast.error(error.message || 'Failed to stop campaign');
        } finally {
            setIsStopping(false);
        }
    };

    const handleActivateClick = () => {
        setShowActivateFlow(true);
    };

    const handleActivated = () => {
        if (campaign) {
            setCampaign({ ...campaign, currentState: 'ACTIVE' });
        }
        setShowActivateFlow(false);
        fetchRuns();
    };

    const handleBackFromActivate = () => {
        setShowActivateFlow(false);
    };

    const handleCloseActivateFlow = () => {
        setShowActivateFlow(false);
    };

    // Open / close the "Add more runs" modal (re-uses ScheduleRunsStep).
    const handleOpenScheduleRunsModal = () => {
        setShowScheduleRunsModal(true);
    };

    const handleCloseScheduleRunsModal = () => {
        setShowScheduleRunsModal(false);
        // Refresh runs after the user is done booking inside the modal.
        fetchRuns();
    };

    // Cancel a single run (refund).
    const handleConfirmCancelRun = async () => {
        if (!campaign || !runToCancel) return;
        const id = (runToCancel.runId ?? runToCancel.id) as number;
        setCancellingRunId(id);
        try {
            await cancelRun(campaign.campaignId, id);
            toast.success('Run cancelled · 1 credit refunded');
            setRuns((prev) =>
                prev.map((r) =>
                    (r.runId ?? r.id) === id
                        ? { ...r, approvalState: 'CANCELLED', state: 'ABORTED' }
                        : r
                )
            );
        } catch (e: any) {
            toast.error(e?.message || 'Failed to cancel run.');
        } finally {
            setCancellingRunId(null);
            setRunToCancel(null);
        }
    };

    // Save route selection
    const handleSaveRouteSelection = async () => {
        if (!campaign || !selectedRouteUuid) return;
        setIsSavingRoute(true);
        try {
            await updateCampaign(campaign.campaignId, { selectedRouteUuid });
            setCampaign({ ...campaign, selectedRouteUuid });
            setRouteSaved(true);
        } catch (error: any) {
            toast.error(error.message || 'Failed to save route selection');
        } finally {
            setIsSavingRoute(false);
        }
    };

    // Helper to create marker icon
    const createCircleIcon = (color: string, scale: number) => {
        if (typeof google !== 'undefined') {
            return {
                path: google.maps.SymbolPath.CIRCLE,
                scale: scale,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
            };
        }
        return undefined;
    };

    // Loading state
    if (isLoading) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <p style={{ color: '#6b7280' }}>Loading campaign details...</p>
                </div>
            </PageSection>
        );
    }

    // Error state
    if (error || !campaign) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={{ padding: '24px' }}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/campaigns')}
                        type="button"
                    >
                        <ArrowLeftIcon /> Back to Campaigns
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <p style={{ color: '#6b7280', fontSize: '16px' }}>Campaign not found.</p>
                    </div>
                </div>
            </PageSection>
        );
    }

    const TypeIcon = typeConfig?.icon;

    const mapContainerStyle = {
        width: '100%',
        height: isMapExpanded ? '100%' : '350px',
        borderRadius: isMapExpanded ? 0 : '12px'
    };

    const renderMap = () => {
        if (!hasApiKey) {
            return (
                <div style={{
                    width: '100%',
                    height: isMapExpanded ? '100%' : '350px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: isMapExpanded ? 0 : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                }}>
                    Google Maps API key not configured
                </div>
            );
        }

        return (
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={getMapZoom()}
                    onLoad={(map) => {
                        mapRef.current = map;
                        setMapLoaded(true);
                    }}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        zoomControl: true
                    }}
                >
                    {isGeoFence && campaign.config?.geoFence && (
                        <>
                            <Marker
                                position={{
                                    lat: campaign.config.geoFence.latitude,
                                    lng: campaign.config.geoFence.longitude
                                }}
                            />
                            <Circle
                                center={{
                                    lat: campaign.config.geoFence.latitude,
                                    lng: campaign.config.geoFence.longitude
                                }}
                                radius={campaign.config.geoFence.radius}
                                options={{
                                    fillColor: '#10b981',
                                    fillOpacity: 0.2,
                                    strokeColor: '#059669',
                                    strokeWeight: 2
                                }}
                            />
                        </>
                    )}

                    {isDrawRoutes && campaign.config?.drawRoutes && mapLoaded && (
                        <>
                            <Marker
                                position={{
                                    lat: campaign.config.drawRoutes.startingPoint.latitude,
                                    lng: campaign.config.drawRoutes.startingPoint.longitude
                                }}
                                icon={createCircleIcon('#10b981', 12)}
                                label={{
                                    text: 'S',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    fontSize: '10px'
                                }}
                                title="Starting Point"
                            />
                            {campaign.config.drawRoutes.waypoints.map((wp: any) => (
                                <Marker
                                    key={`waypoint-${wp.order}`}
                                    position={{ lat: wp.latitude, lng: wp.longitude }}
                                    icon={createCircleIcon('#3b82f6', 14)}
                                    label={{
                                        text: String(wp.order),
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                    }}
                                    title={`Waypoint ${wp.order}`}
                                />
                            ))}
                            {campaign.config.drawRoutes.snappedPath && campaign.config.drawRoutes.snappedPath.length > 1 && (
                                <Polyline
                                    path={campaign.config.drawRoutes.snappedPath}
                                    options={{
                                        strokeColor: '#3b82f6',
                                        strokeOpacity: 0.8,
                                        strokeWeight: 5
                                    }}
                                />
                            )}
                        </>
                    )}

                    {hasTargetReachMap && mapLoaded && (
                        <>
                            <Marker
                                position={{
                                    lat: campaign.config.targetReach.startLat,
                                    lng: campaign.config.targetReach.startLng
                                }}
                                icon={createCircleIcon('#8b5cf6', 12)}
                                label={{
                                    text: 'S',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    fontSize: '10px'
                                }}
                                title="Start Location"
                            />
                            {decodedRoutes.map((route: any, idx: number) => {
                                const isSelected = route.routeUuid === selectedRouteUuid;
                                return route.path.length > 1 && (
                                    <Polyline
                                        key={`tr-route-${route.routeUuid || idx}`}
                                        path={route.path}
                                        options={{
                                            strokeColor: isSelected ? '#8b5cf6' : '#c4b5fd',
                                            strokeOpacity: isSelected ? 1.0 : 0.4,
                                            strokeWeight: isSelected ? 6 : 3,
                                            zIndex: isSelected ? 2 : 1
                                        }}
                                    />
                                );
                            })}
                        </>
                    )}

                    {/* GPS trail from completed runs — rendered for all campaign types, drawn on top.
                        While a replay is focused, the other runs recede so the animated one owns the map. */}
                    {mapLoaded && trailRuns.map(({ run, color, segments, gaps }) => {
                        if (hiddenRunIds.has(run.runId)) return null;
                        if (run.runId === activeReplayRunId) return null; // TrailReplay draws this one
                        const dimmed = activeReplayRunId != null;
                        return (
                            <React.Fragment key={`driver-run-${run.runId}`}>
                                {segments.map((seg, i) => (
                                    <React.Fragment key={`seg-${i}`}>
                                        {!dimmed && (
                                            <Polyline
                                                path={seg}
                                                options={{
                                                    strokeColor: '#ffffff',
                                                    strokeOpacity: 0.9,
                                                    strokeWeight: 7,
                                                    zIndex: 9
                                                }}
                                            />
                                        )}
                                        <Polyline
                                            path={seg}
                                            options={{
                                                strokeColor: color,
                                                strokeOpacity: dimmed ? 0.15 : 1,
                                                strokeWeight: dimmed ? 3 : 4,
                                                zIndex: 10
                                            }}
                                        />
                                    </React.Fragment>
                                ))}
                                {/* Recording gaps: dotted straight connector — "the car was
                                    here, then there; nothing was recorded in between" */}
                                {gaps.map((gap, i) => (
                                    <Polyline
                                        key={`gap-${i}`}
                                        path={gap}
                                        options={{
                                            strokeOpacity: 0,
                                            zIndex: 10,
                                            icons: [
                                                {
                                                    icon: {
                                                        path: 'M 0,-0.1 0,0.1',
                                                        strokeOpacity: dimmed ? 0.15 : 0.75,
                                                        strokeWeight: dimmed ? 3 : 4,
                                                        strokeColor: color,
                                                        scale: 2
                                                    },
                                                    offset: '0',
                                                    repeat: '12px'
                                                }
                                            ]
                                        }}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}

                    {/* Animated replay of the focused run */}
                    {mapLoaded && mapRef.current && activeTrailRun && (
                        <TrailReplay
                            key={`replay-${activeTrailRun.run.runId}`}
                            ref={replayRef}
                            map={mapRef.current}
                            trackPoints={activeTrailRun.run.trackPoints}
                            color={activeTrailRun.color}
                            adMediaUrl={adMediaUrl}
                            adIsVideo={adIsVideo}
                            playing={replayPlaying}
                            speed={replaySpeed}
                            onTick={handleReplayTick}
                            onEnded={handleReplayEnded}
                        />
                    )}
                </GoogleMap>
            </LoadScript>
        );
    };

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc', padding: 0 }}>
            {/* Page Header */}
            <div style={styles.pageHeader}>
                <div style={styles.pageHeaderInner}>
                    <button
                        style={styles.backButton}
                        onClick={() => navigate('/campaigns')}
                        type="button"
                    >
                        <ArrowLeftIcon /> Back to Campaigns
                    </button>
                    <div style={styles.titleRow}>
                        <div>
                            <h1 style={styles.pageTitle}>{campaign.name}</h1>
                            <div style={styles.badgeRow}>
                                <span style={{
                                    ...styles.badge,
                                    backgroundColor: stateColors.bg,
                                    color: stateColors.text
                                }}>
                                    {currentState === 'STOPPED_BY_OWNER' ? 'STOPPED' : currentState === 'NEEDS_RUNS' ? 'NEEDS RUNS' : currentState}
                                </span>
                                {typeConfig && typeConfig.label && (
                                    <span style={{
                                        ...styles.badge,
                                        backgroundColor: typeConfig.color.bg,
                                        color: typeConfig.color.text
                                    }}>
                                        {TypeIcon && <TypeIcon style={{ width: 12, height: 12, marginRight: 4 }} />}
                                        {typeConfig.label}
                                    </span>
                                )}
                                {isTargetReach && campaign.generationStatus && (() => {
                                    const genStatusMap: Record<string, { label: string; bg: string; text: string }> = {
                                        PENDING: { label: 'Generation Pending', bg: '#f59e0b', text: '#ffffff' },
                                        IN_PROGRESS: { label: 'Generating', bg: '#8b5cf6', text: '#ffffff' },
                                        COMPLETED: { label: 'Generated', bg: '#10b981', text: '#ffffff' },
                                        FAILED: { label: 'Generation Failed', bg: '#ef4444', text: '#ffffff' }
                                    };
                                    const gs = genStatusMap[campaign.generationStatus];
                                    if (!gs) return null;
                                    return (
                                        <span style={{
                                            ...styles.badge,
                                            backgroundColor: gs.bg,
                                            color: gs.text
                                        }}>
                                            {gs.label}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                        <div style={styles.titleActions}>
                            {isDraft && (
                                <>
                                    <button
                                        style={styles.editButton}
                                        onClick={() => setEditModalOpen(true)}
                                        title="Edit campaign"
                                        type="button"
                                    >
                                        <PencilAltIcon /> Edit
                                    </button>
                                    <button
                                        style={styles.activateButton}
                                        onClick={handleActivateClick}
                                        title="Activate campaign"
                                        type="button"
                                    >
                                        <PlayIcon /> Activate
                                    </button>
                                    <button
                                        style={styles.deleteButtonHeader}
                                        onClick={() => setShowDeleteConfirm(true)}
                                        title="Delete campaign"
                                        type="button"
                                    >
                                        <TrashIcon /> Delete
                                    </button>
                                </>
                            )}
                            {!isDraft && !isTerminal && (
                                <button
                                    style={styles.stopButton}
                                    onClick={() => setShowStopConfirm(true)}
                                    title="End campaign"
                                    type="button"
                                >
                                    <BanIcon /> End campaign
                                </button>
                            )}
                            {isTerminal && (
                                <button
                                    style={styles.deleteButtonHeader}
                                    onClick={() => setShowDeleteConfirm(true)}
                                    title="Delete campaign"
                                    type="button"
                                >
                                    <TrashIcon /> Delete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div style={styles.contentArea}>
                {/* Activation Flow — slot picker */}
                {showActivateFlow && (
                    <div style={styles.section}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <button
                                style={styles.backFromActivate}
                                onClick={handleBackFromActivate}
                                type="button"
                            >
                                <ArrowLeftIcon /> Back
                            </button>
                            <h2 style={styles.sectionTitle}>Schedule Runs</h2>
                        </div>
                        <ScheduleRunsStep
                            campaignId={campaign.campaignId}
                            onBooked={handleActivated}
                            onCloseModal={handleCloseActivateFlow}
                            suppressNavigate
                        />
                    </div>
                )}

                {/* Target Reach Generation Progress */}
                {!showActivateFlow && isTargetReach && campaign.generationStatus === 'IN_PROGRESS' && campaign.currentWorkflowStep && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}><RouteIcon /> Route Generation</h2>
                        <TargetReachProgress currentStep={campaign.currentWorkflowStep} />
                    </div>
                )}

                {/* Map Section */}
                {!showActivateFlow && hasMap && (
                    <div style={{
                        ...styles.section,
                        ...(isMapExpanded ? {
                            position: 'fixed',
                            inset: 0,
                            zIndex: 2000,
                            margin: 0,
                            border: 'none',
                            borderRadius: 0,
                            height: '100vh',
                            display: 'flex',
                            flexDirection: 'column'
                        } : {})
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <h2 style={styles.sectionTitle}>
                                {!hasBaseMap && hasTrail ? <><RouteIcon /> GPS Trail</> : isGeoFence ? <><MapMarkerIcon /> Location</> : <><RouteIcon /> Route</>}
                            </h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    style={styles.centerMapButton}
                                    onClick={handleCenterMap}
                                    title="Center map"
                                    type="button"
                                >
                                    <CrosshairsIcon /> Center
                                </button>
                                <button
                                    style={styles.centerMapButton}
                                    onClick={() => setIsMapExpanded(v => !v)}
                                    title={isMapExpanded ? 'Exit full screen' : 'Expand map'}
                                    type="button"
                                >
                                    {isMapExpanded
                                        ? <><CompressArrowsAltIcon /> Exit full screen</>
                                        : <><ExpandArrowsAltIcon /> Expand</>}
                                </button>
                            </div>
                        </div>
                        <div style={{
                            position: 'relative',
                            borderRadius: isMapExpanded ? 0 : '12px',
                            overflow: 'hidden',
                            border: isMapExpanded ? 'none' : '1px solid #e5e7eb',
                            ...(isMapExpanded ? { flex: 1, minHeight: 0 } : {})
                        }}>
                            {renderMap()}

                            {/* Floating replay controls */}
                            {activeTrailRun && (
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '16px',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '10px 16px',
                                    borderRadius: '999px',
                                    background: 'rgba(15, 23, 42, 0.78)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.35)',
                                    color: '#ffffff',
                                    width: 'min(560px, calc(100% - 32px))',
                                    animation: 'advReplayBarIn 0.35s ease both',
                                    zIndex: 5
                                }}>
                                    <button
                                        type="button"
                                        onClick={handleReplayPlayPause}
                                        title={replayPlaying ? 'Pause replay' : 'Play replay'}
                                        style={{
                                            width: '34px',
                                            height: '34px',
                                            flexShrink: 0,
                                            borderRadius: '50%',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: activeTrailRun.color,
                                            color: '#ffffff'
                                        }}
                                    >
                                        {replayPlaying ? <PauseIcon /> : <PlayIcon style={{ marginLeft: 2 }} />}
                                    </button>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                                        <input
                                            type="range"
                                            min={0}
                                            max={1000}
                                            value={Math.round(replayProgress * 1000)}
                                            onChange={(e) => handleReplaySeek(Number(e.currentTarget.value) / 1000)}
                                            aria-label="Replay position"
                                            style={{
                                                width: '100%',
                                                accentColor: activeTrailRun.color,
                                                cursor: 'pointer',
                                                height: '4px'
                                            }}
                                        />
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: 'rgba(255,255,255,0.85)',
                                            fontVariantNumeric: 'tabular-nums'
                                        }}>
                                            <span>
                                                {activeTrailRun.slotStart ? formatRunSlot(activeTrailRun.slotStart) : `Run #${activeTrailRun.run.runId}`}
                                            </span>
                                            <span>
                                                {replayWallMs != null ? formatWallClock(replayWallMs) : `${Math.round(replayProgress * 100)}%`}
                                                {replayTotalMs > 60000 && replayWallMs != null ? ` · ${formatRunDuration(replayTotalMs)} run` : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={cycleReplaySpeed}
                                        title="Replay speed"
                                        style={{
                                            flexShrink: 0,
                                            border: '1px solid rgba(255,255,255,0.25)',
                                            background: 'rgba(255,255,255,0.08)',
                                            color: '#ffffff',
                                            borderRadius: '999px',
                                            padding: '4px 10px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            fontVariantNumeric: 'tabular-nums'
                                        }}
                                    >
                                        {replaySpeed}×
                                    </button>
                                    <button
                                        type="button"
                                        onClick={exitReplay}
                                        title="Exit replay"
                                        style={{
                                            flexShrink: 0,
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            border: 'none',
                                            background: 'rgba(255,255,255,0.12)',
                                            color: '#ffffff',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <TimesIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                        {hasTrail && (
                            <>
                                <style>{`
@keyframes advReplayBarIn { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
@keyframes advEqBar { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
`}</style>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '14px' }}>
                                    {trailRuns.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={activeReplayRunId != null ? exitReplay : handleReplayAll}
                                            title="Replay every run in order"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '6px 14px',
                                                borderRadius: '999px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '12.5px',
                                                fontWeight: 700,
                                                color: '#ffffff',
                                                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                                                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)'
                                            }}
                                        >
                                            {activeReplayRunId != null ? <><TimesIcon style={{ width: 11, height: 11 }} /> Stop</> : <><PlayIcon style={{ width: 11, height: 11 }} /> Replay campaign</>}
                                        </button>
                                    )}
                                    {trailRuns.map(({ run, color, slotStart }) => {
                                        const label = slotStart ? formatRunSlot(slotStart) : `Run #${run.runId}`;
                                        const hidden = hiddenRunIds.has(run.runId);
                                        const active = run.runId === activeReplayRunId;
                                        return (
                                            <button
                                                key={`trail-legend-${run.runId}`}
                                                type="button"
                                                onClick={() => handleChipClick(run.runId)}
                                                title={active ? 'Exit replay' : 'Replay this run'}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '7px',
                                                    fontSize: '12.5px',
                                                    fontWeight: 600,
                                                    color: hidden ? '#9ca3af' : active ? color : '#374151',
                                                    background: active ? `${color}14` : '#ffffff',
                                                    border: `1px solid ${active ? color : '#e5e7eb'}`,
                                                    borderRadius: '999px',
                                                    padding: '6px 12px',
                                                    cursor: 'pointer',
                                                    opacity: hidden ? 0.55 : 1,
                                                    textDecoration: hidden ? 'line-through' : 'none',
                                                    boxShadow: active ? `0 2px 10px ${color}40` : '0 1px 2px rgba(15,23,42,0.06)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {active && replayPlaying ? (
                                                    // Tiny "equalizer" while this run is animating
                                                    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px', height: '10px' }}>
                                                        {[0, 1, 2].map((i) => (
                                                            <span key={i} style={{
                                                                width: '2.5px',
                                                                height: '10px',
                                                                borderRadius: '2px',
                                                                background: color,
                                                                transformOrigin: 'bottom',
                                                                animation: `advEqBar 0.9s ease-in-out ${i * 0.15}s infinite`
                                                            }} />
                                                        ))}
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '9px',
                                                        height: '9px',
                                                        borderRadius: '50%',
                                                        backgroundColor: hidden ? '#d1d5db' : color
                                                    }} />
                                                )}
                                                {label}
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.stopPropagation(); toggleRunVisibility(run.runId); }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleRunVisibility(run.runId);
                                                        }
                                                    }}
                                                    title={hidden ? 'Show this trail' : 'Hide this trail'}
                                                    style={{
                                                        marginLeft: '2px',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: hidden ? '#9ca3af' : '#9ca3af',
                                                        padding: '0 2px',
                                                        lineHeight: 1
                                                    }}
                                                >
                                                    {hidden ? '◌' : '●'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {trailRuns.length > 0 && activeReplayRunId == null && (
                                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                                        Click a run to watch the car replay it with your ad on the rooftop screen.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Target Reach: Route Selection */}
                {!showActivateFlow && isTargetReach && decodedRoutes.length > 0 && (
                    <div style={styles.section}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
                                <RouteIcon /> {canSelectRoute ? 'Select Route' : 'Selected Route'}
                            </h2>
                            {!canSelectRoute && (
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Locked
                                </span>
                            )}
                        </div>
                        {canSelectRoute && decodedRoutes.length > 1 && (
                            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px 0' }}>
                                Choose the route your driver will follow. Click a route to preview it on the map, then save your selection.
                            </p>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {decodedRoutes.map((route: any, idx: number) => {
                                const isSelected = route.routeUuid === selectedRouteUuid;
                                const isPrimary = route.rankOrder === 1;
                                const isClickable = canSelectRoute && decodedRoutes.length > 1;
                                const stats: any = safeParseJson(route.statsJson);
                                const routeConfidenceColor = confidenceColor(stats?.confidence_score ?? 0);
                                return (
                                    <div
                                        key={route.routeUuid || idx}
                                        onClick={() => {
                                            if (!isClickable) return;
                                            setSelectedRouteUuid(route.routeUuid);
                                            setRouteSaved(false);
                                        }}
                                        style={{
                                            padding: '14px 16px',
                                            borderRadius: '8px',
                                            backgroundColor: isSelected ? '#faf5ff' : '#ffffff',
                                            border: isSelected ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                                            cursor: isClickable ? 'pointer' : 'default',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                {/* Radio indicator */}
                                                <div style={{
                                                    width: '18px',
                                                    height: '18px',
                                                    borderRadius: '50%',
                                                    border: isSelected ? '2px solid #8b5cf6' : '2px solid #d1d5db',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    {isSelected && (
                                                        <div style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#8b5cf6'
                                                        }} />
                                                    )}
                                                </div>
                                                <div>
                                                    <span style={{
                                                        fontSize: '14px',
                                                        fontWeight: isSelected ? 600 : 500,
                                                        color: isSelected ? '#5b21b6' : '#374151'
                                                    }}>
                                                        {isPrimary ? 'Primary Route' : `Alternate Route ${route.rankOrder - 1}`}
                                                    </span>
                                                    {isPrimary && (
                                                        <span style={{
                                                            display: 'inline-block',
                                                            marginLeft: '8px',
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            padding: '1px 6px',
                                                            borderRadius: '3px',
                                                            backgroundColor: '#ede9fe',
                                                            color: '#7c3aed',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            verticalAlign: 'middle'
                                                        }}>
                                                            Recommended
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Stats row */}
                                        <div style={{
                                            display: 'flex',
                                            gap: '16px',
                                            flexWrap: 'wrap',
                                            marginTop: '10px',
                                            marginLeft: '30px',
                                            fontSize: '13px',
                                            color: '#6b7280'
                                        }}>
                                            {route.distanceM != null && (
                                                <span><strong style={{ color: '#374151' }}>{(route.distanceM / 1609.34).toFixed(1)}</strong> mi</span>
                                            )}
                                            {route.durationS != null && (
                                                <span><strong style={{ color: '#374151' }}>{Math.round(route.durationS / 60)}</strong> min</span>
                                            )}
                                            {stats?.total_impressions != null && (
                                                <span><strong style={{ color: '#374151' }}>{stats.total_impressions.toLocaleString()}</strong> impressions</span>
                                            )}
                                            {stats?.cohort_impressions != null && (
                                                <span><strong style={{ color: '#374151' }}>{stats.cohort_impressions.toLocaleString()}</strong> cohort</span>
                                            )}
                                            {stats?.confidence_score != null && (
                                                <span>
                                                    <strong style={{ color: routeConfidenceColor }}>{(stats.confidence_score * 100).toFixed(0)}%</strong>
                                                    {' '}confidence
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && parsedResult?.explanation && (
                                            <p style={{
                                                margin: '10px 0 0 30px',
                                                fontSize: '13px',
                                                color: '#6b7280',
                                                lineHeight: 1.6
                                            }}>
                                                {parsedResult.explanation}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {canSelectRoute && decodedRoutes.length > 1 && (
                            <div style={{ marginTop: '16px' }}>
                                {routeSaved ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        backgroundColor: '#dcfce7',
                                        color: '#166534',
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}>
                                        <CheckCircleIcon />
                                        Route Saved
                                        <span style={{ fontWeight: 400, fontSize: '13px', color: '#15803d' }}>
                                            — Selection confirmed
                                        </span>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleSaveRouteSelection}
                                        disabled={isSavingRoute}
                                        type="button"
                                        style={{
                                            padding: '10px 24px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: isSavingRoute ? '#a78bfa' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                            color: '#ffffff',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: isSavingRoute ? 'not-allowed' : 'pointer',
                                            opacity: isSavingRoute ? 0.7 : 1,
                                            transition: 'opacity 0.15s ease'
                                        }}
                                    >
                                        {isSavingRoute ? 'Saving...' : 'Save Route Selection'}
                                    </button>
                                )}
                            </div>
                        )}
                        {decodedRoutes.length === 1 && (
                            <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: '12px 0 0 0' }}>
                                Only one route was generated — it will be used automatically.
                            </p>
                        )}
                    </div>
                )}

                {/* Location/Route Info */}
                {!showActivateFlow && isGeoFence && campaign.config?.geoFence && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}><MapMarkerIcon /> Location Details</h2>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Address</span>
                                <span style={styles.infoValue}>{campaign.config.geoFence.address}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Radius</span>
                                <span style={styles.infoValue}>{campaign.config.geoFence.radius}m</span>
                            </div>
                        </div>
                    </div>
                )}

                {!showActivateFlow && isDrawRoutes && campaign.config?.drawRoutes && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}><RouteIcon /> Route Details</h2>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Starting Point</span>
                                <span style={styles.infoValue}>{campaign.config.drawRoutes.startingPoint.address}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Waypoints</span>
                                <span style={styles.infoValue}>{campaign.config.drawRoutes.waypoints.length}</span>
                            </div>
                            {campaign.config.drawRoutes.totalDistanceText && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Total Distance</span>
                                    <span style={styles.infoValue}>{campaign.config.drawRoutes.totalDistanceText}</span>
                                </div>
                            )}
                            {campaign.config.drawRoutes.isValidated && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Status</span>
                                    <span style={styles.infoValue}>Validated</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Runs */}
                {!showActivateFlow && !isTargetReach && (
                    <div style={styles.section}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
                                <ClockIcon /> Runs ({runs.length})
                            </h2>
                            {!isTerminal && (
                                <button
                                    style={styles.activateButton}
                                    onClick={handleOpenScheduleRunsModal}
                                    type="button"
                                >
                                    <PlayIcon /> Add more runs
                                </button>
                            )}
                        </div>

                        {runsLoading && runs.length === 0 ? (
                            <div style={{ padding: '20px', color: '#6b7280', fontSize: 13 }}>Loading runs…</div>
                        ) : runs.length === 0 ? (
                            <div style={{ padding: '20px', color: '#6b7280', fontSize: 14 }}>
                                No runs booked yet. Click <strong>Add more runs</strong> to book a slot.
                            </div>
                        ) : (
                            <RunsTable
                                runs={runs}
                                cancellingRunId={cancellingRunId}
                                onCancelClick={(r) => setRunToCancel(r)}
                            />
                        )}
                    </div>
                )}

                {/* Target Reach: Campaign Configuration */}
                {!showActivateFlow && isTargetReach && campaign.config?.targetReach && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}><RouteIcon /> Campaign Configuration</h2>
                        {campaign.config.targetReach.userPrompt && (
                            <div style={{
                                borderLeft: '4px solid #8b5cf6',
                                padding: '12px 16px',
                                backgroundColor: '#faf5ff',
                                borderRadius: '0 8px 8px 0',
                                marginBottom: '16px',
                                fontSize: '14px',
                                color: '#4c1d95',
                                fontStyle: 'italic',
                                lineHeight: 1.6
                            }}>
                                {campaign.config.targetReach.userPrompt}
                            </div>
                        )}
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Start Location</span>
                                <span style={styles.infoValue}>
                                    {campaign.config.targetReach.startLat.toFixed(4)}, {campaign.config.targetReach.startLng.toFixed(4)}
                                </span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Max Hours/Day</span>
                                <span style={styles.infoValue}>{campaign.config.targetReach.maxDrivingHoursPerDay}h</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Budget Days</span>
                                <span style={styles.infoValue}>{campaign.config.targetReach.budgetDays} days</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Road Preference</span>
                                <span style={{ ...styles.infoValue, textTransform: 'capitalize' }}>{campaign.config.targetReach.roadPreference}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Measured Reach (actual post-campaign reach from analyzed runs) */}
                {MEASURED_REACH_ENABLED && !showActivateFlow && (measuredReach || perRunMeasuredReach.length > 0 || measuredReachInProgress || measuredReachFailed) && (
                    <div style={styles.section}>
                        <style>{`@keyframes mrSpin { to { transform: rotate(360deg); } }`}</style>
                        <h2 style={styles.sectionTitle}>
                            <CrosshairsIcon /> Measured Reach
                            {measuredReachInProgress && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#1e40af', background: '#dbeafe', border: '1px solid #bfdbfe', borderRadius: '999px', padding: '2px 10px' }}>
                                    <span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid #bfdbfe', borderTopColor: '#1e40af', borderRadius: '50%', animation: 'mrSpin 0.8s linear infinite' }} />
                                    Calculating…
                                </span>
                            )}
                        </h2>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px 0' }}>
                            Actual reach measured from completed runs&apos; GPS traces.
                        </p>

                        {measuredReach ? (
                            <>
                            {/* Drive stats describe the whole analysis, not either metric — keep them in a shared strip. */}
                            {(measuredReach.distance_m != null || measuredReach.total_dwell_hours != null || measuredReach.run_count != null || measuredReach.confidence_score != null) && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', alignItems: 'baseline', fontSize: '13px', color: '#6b7280', margin: '0 0 18px 0' }}>
                                    {measuredReach.distance_m != null && (
                                        <span><strong style={{ color: '#374151' }}>{(measuredReach.distance_m / METERS_PER_MILE).toFixed(1)}</strong> mi</span>
                                    )}
                                    {measuredReach.total_dwell_hours != null && (
                                        <span><strong style={{ color: '#374151' }}>{measuredReach.total_dwell_hours.toFixed(1)}</strong> hrs dwell</span>
                                    )}
                                    {measuredReach.run_count != null && (
                                        <span><strong style={{ color: '#374151' }}>{measuredReach.run_count}</strong> runs analyzed</span>
                                    )}
                                    {measuredReach.confidence_score != null && (
                                        <span>
                                            <strong style={{ color: confidenceColor(measuredReach.confidence_score) }}>{(measuredReach.confidence_score * 100).toFixed(0)}%</strong>
                                            {measuredReach.confidence_label ? <span style={{ textTransform: 'capitalize' }}> {measuredReach.confidence_label}</span> : ''} confidence
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Two metrics, side by side: co-presence (exposures) vs population (unique residents). */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                <div style={{ flex: '1 1 280px', minWidth: 0, border: '1px solid #e5e7eb', borderLeft: '3px solid #2563eb', borderRadius: '10px', padding: '16px' }}>
                                    <Tooltip
                                        entryDelay={0}
                                        exitDelay={0}
                                        animationDuration={120}
                                        content="Vehicle exposures: how many vehicles were physically near the ad while it was visible, from AADT × dwell. Repeat passes over a road count again (impressions, not unique people)."
                                    >
                                        <div
                                            style={{ ...styles.metricLabel, cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <CarIcon style={{ color: '#2563eb' }} />
                                            Co-presence
                                        </div>
                                    </Tooltip>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 12px 0' }}>Vehicle exposures</div>
                                    {measuredReach.total_impressions != null && metricRow('Total', measuredReach.total_impressions.toLocaleString(), true)}
                                    {measuredReach.cohort_impressions && Object.entries(measuredReach.cohort_impressions).map(([key, value]) => (
                                        <React.Fragment key={`imp-${key}`}>{metricRow(formatCohortKey(key), Number(value).toLocaleString())}</React.Fragment>
                                    ))}
                                </div>

                                {(measuredReach.population_along_route != null || measuredReach.cohort_population) && (
                                    <div style={{ flex: '1 1 280px', minWidth: 0, border: '1px solid #e5e7eb', borderLeft: '3px solid #7c3aed', borderRadius: '10px', padding: '16px' }}>
                                        <Tooltip
                                            entryDelay={0}
                                            exitDelay={0}
                                            animationDuration={120}
                                            content="Unique residents living near the route, counted once (a road driven twice is not double-counted within a run). Apportioned from Census block-group population inside the buffer. High income = residents in top-quartile-income areas. Note: per-run figures are summed at the campaign level, so residents covered by more than one run can be counted more than once across runs."
                                        >
                                            <div
                                                style={{ ...styles.metricLabel, cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <UsersIcon style={{ color: '#7c3aed' }} />
                                                Population Along Route
                                            </div>
                                        </Tooltip>
                                        <div style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 12px 0' }}>
                                            Unique residents
                                            {measuredReach.population_buffer_m ? ` within ${Math.round(measuredReach.population_buffer_m)} m` : ''}
                                            {measuredReach.population_bg_count != null ? ` · ${measuredReach.population_bg_count.toLocaleString()} block groups` : ''}
                                        </div>
                                        {measuredReach.population_along_route != null && metricRow('Residents', measuredReach.population_along_route.toLocaleString(), true)}
                                        {measuredReach.cohort_population && Object.entries(measuredReach.cohort_population).map(([key, value]) => (
                                            <React.Fragment key={`pop-${key}`}>{metricRow(formatCohortKey(key), Number(value).toLocaleString())}</React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </>
                        ) : (
                            <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                                {measuredReachInProgress
                                    ? 'Measuring reach from the runs’ GPS traces… this can take a few minutes.'
                                    : measuredReachFailed
                                        ? 'We couldn’t measure reach for some runs. Totals will appear once a run is successfully analyzed.'
                                        : 'Campaign-level totals will appear once all completed runs have been analyzed.'}
                            </p>
                        )}

                        {perRunMeasuredReach.length > 0 && (
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    margin: '0 0 12px 0'
                                }}>
                                    Per-Run Breakdown
                                </h3>
                                <div style={{ ...perRunGrid, padding: '0 14px 8px 14px', fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <span>Run</span>
                                    <span style={{ textAlign: 'right' }}>Impressions</span>
                                    <span style={{ textAlign: 'right' }}>Residents</span>
                                    <span style={{ textAlign: 'right' }}>Distance</span>
                                    <span style={{ textAlign: 'right' }}>Confidence</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {perRunMeasuredReach.map(({ run, mr }) => (
                                        <div key={run.runId} style={{
                                            ...perRunGrid,
                                            alignItems: 'center',
                                            padding: '12px 14px',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '8px',
                                            fontSize: '13px'
                                        }}>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>
                                                {run.slotStart ? formatRunSlot(run.slotStart) : `Run #${run.runId}`}
                                            </span>
                                            <span style={{ textAlign: 'right', color: '#374151' }}>
                                                {mr.total_impressions != null ? <strong>{mr.total_impressions.toLocaleString()}</strong> : <span style={{ color: '#d1d5db' }}>—</span>}
                                            </span>
                                            <span style={{ textAlign: 'right', color: '#374151' }}>
                                                {mr.population_along_route != null ? <strong>{mr.population_along_route.toLocaleString()}</strong> : <span style={{ color: '#d1d5db' }}>—</span>}
                                            </span>
                                            <span style={{ textAlign: 'right', color: '#6b7280' }}>
                                                {mr.distance_m != null ? `${(mr.distance_m / METERS_PER_MILE).toFixed(1)} mi` : <span style={{ color: '#d1d5db' }}>—</span>}
                                            </span>
                                            <span style={{ textAlign: 'right' }}>
                                                {mr.confidence_score != null ? <strong style={{ color: confidenceColor(mr.confidence_score) }}>{(mr.confidence_score * 100).toFixed(0)}%</strong> : <span style={{ color: '#d1d5db' }}>—</span>}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Ads */}
                {!showActivateFlow && campaign.ads && campaign.ads.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}><CubesIcon /> Ads ({campaign.ads.length})</h2>
                        <div style={styles.adsList}>
                            {campaign.ads.map((ad: any) => (
                                <div
                                    key={ad.adId}
                                    style={styles.adItem}
                                    onClick={() => navigate('/ads/' + ad.adId)}
                                >
                                    {ad.presignedUrl ? (
                                        <img
                                            src={ad.presignedUrl}
                                            alt={ad.name}
                                            style={styles.adImage}
                                        />
                                    ) : (
                                        <div style={styles.adImagePlaceholder}>
                                            {(ad.name || 'A').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div style={styles.adInfo}>
                                        <div style={styles.adName}>{ad.name}</div>
                                        {ad.organizationName && (
                                            <div style={styles.adOrg}>{ad.organizationName}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Details Grid */}
                {!showActivateFlow && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Details</h2>
                        <div style={styles.infoGrid}>
                            {campaign.organizationName && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}><BuildingIcon /> Organization</span>
                                    <span style={styles.infoValue}>{campaign.organizationName}</span>
                                </div>
                            )}
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Campaign Type</span>
                                <span style={styles.infoValue}>{typeConfig?.label || campaign.campaignType || 'Unknown'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Status</span>
                                <span style={styles.infoValue}>{currentState === 'STOPPED_BY_OWNER' ? 'Stopped' : currentState === 'NEEDS_RUNS' ? 'Needs runs' : currentState}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}><CubesIcon /> Ads</span>
                                <span style={styles.infoValue}>{campaign.ads?.length || 0} ad{(campaign.ads?.length || 0) !== 1 ? 's' : ''}</span>
                            </div>
                            {campaign.createDateTime && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}><CalendarAltIcon /> Created</span>
                                    <span style={styles.infoValue}>{formatDate(campaign.createDateTime)}</span>
                                </div>
                            )}
                            {campaign.updateDateTime && (
                                <div style={styles.infoItem}>
                                    <span style={styles.infoLabel}><CalendarAltIcon /> Last Updated</span>
                                    <span style={styles.infoValue}>{formatDate(campaign.updateDateTime)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Footer */}
                {!showActivateFlow && (
                    <div style={styles.footer}>
                        <span style={styles.campaignIdText}>ID: {campaign.campaignId}</span>
                    </div>
                )}
            </div>

            {/* Edit Campaign Modal */}
            <EditCampaign
                campaign={campaign}
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSave={() => { fetchCampaign(); }}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={styles.confirmOverlay} onClick={() => setShowDeleteConfirm(false)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>Delete Campaign</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to delete &quot;{campaign.name}&quot;? This action cannot be undone.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button
                                style={styles.confirmCancelButton}
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...styles.confirmDeleteButton,
                                    ...(isDeleting ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                                }}
                                onClick={handleDelete}
                                disabled={isDeleting}
                                type="button"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add-more-runs modal */}
            <AppModal
                isOpen={showScheduleRunsModal}
                onClose={handleCloseScheduleRunsModal}
                width="xl"
            >
                <div style={{ padding: '28px 28px 24px' }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 16px 0', color: '#0f172a' }}>
                        Schedule More Runs
                    </h2>
                    <ScheduleRunsStep
                        campaignId={campaign.campaignId}
                        onBooked={handleCloseScheduleRunsModal}
                        onCloseModal={handleCloseScheduleRunsModal}
                        suppressNavigate
                    />
                </div>
            </AppModal>

            {/* Cancel-run confirmation */}
            {runToCancel && (
                <div style={styles.confirmOverlay} onClick={() => setRunToCancel(null)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>Cancel run</h3>
                        <p style={styles.confirmText}>
                            Refund 1 credit and cancel this run?
                        </p>
                        <div style={styles.confirmButtons}>
                            <button
                                style={styles.confirmCancelButton}
                                onClick={() => setRunToCancel(null)}
                                disabled={cancellingRunId !== null}
                                type="button"
                            >
                                Keep run
                            </button>
                            <button
                                style={{
                                    ...styles.confirmDeleteButton,
                                    ...(cancellingRunId !== null ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                                }}
                                onClick={handleConfirmCancelRun}
                                disabled={cancellingRunId !== null}
                                type="button"
                            >
                                {cancellingRunId !== null ? 'Cancelling…' : 'Cancel & Refund'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* End Campaign Confirmation Modal */}
            {showStopConfirm && (
                <div style={styles.confirmOverlay} onClick={() => setShowStopConfirm(false)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>End campaign</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to end &quot;{campaign.name}&quot;? This finalizes the campaign. Any runs that haven&apos;t started yet are cancelled and refunded; runs already in progress are stopped.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button
                                style={styles.confirmCancelButton}
                                onClick={() => setShowStopConfirm(false)}
                                disabled={isStopping}
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...styles.confirmStopButton,
                                    ...(isStopping ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                                }}
                                onClick={handleStop}
                                disabled={isStopping}
                                type="button"
                            >
                                {isStopping ? 'Ending...' : 'End campaign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageSection>
    );
};

const styles: Record<string, React.CSSProperties> = {
    pageHeader: {
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
    },
    pageHeaderInner: {
        maxWidth: '100%'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 0',
        fontSize: '14px',
        fontWeight: 500,
        border: 'none',
        background: 'none',
        color: '#667eea',
        cursor: 'pointer',
        marginBottom: '16px',
        transition: 'color 0.2s'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
    },
    pageTitle: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    badgeRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 14px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    titleActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0
    },
    editButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'opacity 0.2s'
    },
    activateButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'opacity 0.2s',
        boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)'
    },
    deleteButtonHeader: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: '#ffffff',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        padding: '8px 18px',
        cursor: 'pointer',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'all 0.2s'
    },
    stopButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: '#f97316',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        cursor: 'pointer',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'opacity 0.2s'
    },
    contentArea: {
        padding: '32px'
    },
    section: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#374151',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    centerMapButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        fontSize: '13px',
        fontWeight: 500,
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px'
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    infoLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    infoValue: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1f2937'
    },
    adsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    adItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    adImage: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        objectFit: 'cover',
        backgroundColor: '#e5e7eb'
    },
    adImagePlaceholder: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '18px',
        fontWeight: 700
    },
    adInfo: {
        flex: 1
    },
    adName: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#1f2937'
    },
    adOrg: {
        fontSize: '12px',
        color: '#6b7280'
    },
    footer: {
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb'
    },
    campaignIdText: {
        fontSize: '12px',
        color: '#9ca3af',
        fontFamily: 'monospace'
    },
    backFromActivate: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        fontSize: '14px',
        fontWeight: 500,
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    // Confirmation modal styles
    confirmOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
    },
    confirmModal: {
        background: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    confirmTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0'
    },
    confirmText: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 20px 0',
        lineHeight: 1.5
    },
    confirmButtons: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
    },
    confirmCancelButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 500,
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        color: '#374151',
        cursor: 'pointer'
    },
    confirmDeleteButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '8px',
        background: '#dc2626',
        color: '#ffffff',
        cursor: 'pointer'
    },
    confirmStopButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '8px',
        background: '#f97316',
        color: '#ffffff',
        cursor: 'pointer'
    },
    metricCard: {
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        textAlign: 'center'
    },
    metricValue: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: '4px'
    },
    metricLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    }
};

// --- Runs table ---

interface RunsTableProps {
    runs: CampaignRun[];
    cancellingRunId: number | null;
    onCancelClick: (run: CampaignRun) => void;
}

type RunProgressInfo = {
    step: 1 | 2 | 3 | 4;
    terminal: 'ok' | 'failed' | null;
    pill: { bg: string; color: string; label: string };
};

const getRunProgress = (run: CampaignRun): RunProgressInfo => {
    const { approvalState, state } = run;

    // Terminal failure paths — dot color goes red, step held at last reached.
    if (approvalState === 'DECLINED') {
        return {
            step: 1,
            terminal: 'failed',
            pill: { bg: '#fee2e2', color: '#991b1b', label: 'Declined' }
        };
    }
    if (approvalState === 'NO_DRIVER_AVAILABLE') {
        return {
            step: 1,
            terminal: 'failed',
            pill: { bg: '#f3f4f6', color: '#4b5563', label: 'No driver available' }
        };
    }
    if (approvalState === 'CANCELLED') {
        // Cancelled can happen pre- or post-approval; treat as failure at step 2 when previously approved.
        const reachedApproved = state === 'PENDING' || state === 'IN_PROGRESS' || state === 'PAUSED';
        return {
            step: reachedApproved ? 2 : 1,
            terminal: 'failed',
            pill: { bg: '#f3f4f6', color: '#4b5563', label: 'Cancelled' }
        };
    }
    if (state === 'ABORTED') {
        return {
            step: 3,
            terminal: 'failed',
            pill: { bg: '#fee2e2', color: '#991b1b', label: 'Aborted' }
        };
    }

    // Happy-path progression.
    if (state === 'COMPLETED') {
        return {
            step: 4,
            terminal: 'ok',
            pill: { bg: '#ede9fe', color: '#5b21b6', label: 'Completed' }
        };
    }
    if (state === 'IN_PROGRESS') {
        return {
            step: 3,
            terminal: null,
            pill: { bg: '#dbeafe', color: '#1e40af', label: 'In progress' }
        };
    }
    if (state === 'PAUSED') {
        return {
            step: 3,
            terminal: null,
            pill: { bg: '#fef3c7', color: '#92400e', label: 'Paused' }
        };
    }
    if (approvalState === 'APPROVED') {
        return {
            step: 2,
            terminal: null,
            pill: { bg: '#dcfce7', color: '#166534', label: 'Approved · awaiting slot' }
        };
    }
    if (approvalState === 'PENDING_APPROVAL') {
        return {
            step: 1,
            terminal: null,
            pill: { bg: '#fef3c7', color: '#92400e', label: 'Pending approval' }
        };
    }

    // Unknown — surface the raw value rather than swallowing it.
    return {
        step: 1,
        terminal: null,
        pill: { bg: '#f3f4f6', color: '#4b5563', label: approvalState }
    };
};

const RUN_PROGRESS_STEPS: Array<{ label: string }> = [
    { label: 'Pending' },
    { label: 'Appr.' },
    { label: 'Run' },
    { label: 'Done' }
];

const RunProgress: React.FC<{ run: CampaignRun }> = ({ run }) => {
    const info = getRunProgress(run);
    const isNoDriver = run.approvalState === 'NO_DRIVER_AVAILABLE';
    const reachedColor = info.terminal === 'failed' ? '#dc2626' : '#10b981';
    const futureColor = '#d1d5db';
    const STEP_WIDTH = 64;

    return (
        <div style={{ display: 'inline-block' }}>
            {/* Dots + connectors row */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {RUN_PROGRESS_STEPS.map((_, idx) => {
                    const stepIndex = idx + 1;
                    const isReached = stepIndex < info.step;
                    const isCurrent = stepIndex === info.step;
                    const dotColor = isReached || isCurrent ? reachedColor : futureColor;
                    const ringStyle: React.CSSProperties = isCurrent
                        ? { boxShadow: `0 0 0 3px ${reachedColor}33` }
                        : {};
                    return (
                        <React.Fragment key={stepIndex}>
                            <div style={{ width: STEP_WIDTH, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                <span
                                    style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        background: dotColor,
                                        display: 'inline-block',
                                        ...ringStyle
                                    }}
                                />
                            </div>
                            {stepIndex < RUN_PROGRESS_STEPS.length && (
                                <span
                                    style={{
                                        flex: 1,
                                        height: 2,
                                        background: isReached ? reachedColor : futureColor,
                                        minWidth: 8
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {/* Labels row — column widths mirror the dot row so labels align under their dots */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 6, marginBottom: 8 }}>
                {RUN_PROGRESS_STEPS.map((s, idx) => {
                    const stepIndex = idx + 1;
                    const isCurrent = stepIndex === info.step;
                    return (
                        <React.Fragment key={stepIndex}>
                            <div style={{
                                width: STEP_WIDTH,
                                textAlign: 'center',
                                fontSize: 10,
                                fontWeight: isCurrent ? 700 : 500,
                                color: isCurrent ? '#111827' : '#6b7280',
                                letterSpacing: '0.3px',
                                flexShrink: 0
                            }}>
                                {s.label}
                            </div>
                            {stepIndex < RUN_PROGRESS_STEPS.length && (
                                <div style={{ flex: 1, minWidth: 8 }} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                background: info.pill.bg,
                color: info.pill.color
            }}>
                {info.pill.label}
            </span>
            {isNoDriver && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#059669' }}>
                    Refunded · 1 credit
                </div>
            )}
        </div>
    );
};

const formatRunSlot = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    } catch {
        return iso;
    }
};

const RunsTable: React.FC<RunsTableProps> = ({ runs, cancellingRunId, onCancelClick }) => {
    // Sort by slotStart ascending (soonest first).
    const sorted = [...runs].sort((a, b) => {
        const at = new Date(a.slotStart).getTime();
        const bt = new Date(b.slotStart).getTime();
        return at - bt;
    });

    const tableStyle: React.CSSProperties = {
        width: '100%',
        borderCollapse: 'collapse'
    };
    const thStyle: React.CSSProperties = {
        padding: '10px 12px',
        textAlign: 'left',
        fontSize: 11,
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
    };
    const tdStyle: React.CSSProperties = {
        padding: '12px',
        fontSize: 14,
        color: '#111827',
        borderBottom: '1px solid #e5e7eb',
        verticalAlign: 'middle'
    };

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Slot Start</th>
                        <th style={thStyle}>Progress</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => {
                        const id = (r.runId ?? r.id) as number;
                        const isRunTerminal = r.state === 'COMPLETED' || r.state === 'ABORTED';
                        const canCancel =
                            (r.approvalState === 'PENDING_APPROVAL' || r.approvalState === 'APPROVED') &&
                            !isRunTerminal;
                        return (
                            <tr key={id}>
                                <td style={tdStyle}>{formatRunSlot(r.slotStart)}</td>
                                <td style={tdStyle}>
                                    <RunProgress run={r} />
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                    {canCancel ? (
                                        <button
                                            type="button"
                                            onClick={() => onCancelClick(r)}
                                            disabled={cancellingRunId === id}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8,
                                                background: '#ffffff',
                                                color: '#dc2626',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                cursor: cancellingRunId === id ? 'not-allowed' : 'pointer',
                                                opacity: cancellingRunId === id ? 0.6 : 1
                                            }}
                                        >
                                            {cancellingRunId === id ? 'Cancelling…' : 'Cancel'}
                                        </button>
                                    ) : (
                                        <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export { CampaignDetailPage };
